import { track } from 'lib/sdks/analytics';
import { JOB_TYPE, type JobType } from 'lib/sdks/jobs-management/job.constants';
import type useFunnelling from '../hooks/useFunnelling';
import { logBrazeEvent } from 'lib/sdks/braze/braze';
import { setConsumerCookie } from 'lib/utils/cookie';

const JOB_TRACKING_TYPES = {
  [JOB_TYPE.requestAQuote]: 'inline_send_a_job',
  [JOB_TYPE.directMessage]: 'message',
} as const;

const CONVERT_TYPE = {
  [JOB_TYPE.requestAQuote]: 'raq',
  [JOB_TYPE.directMessage]: 'direct_message',
} as const;

const JOB_TYPES = {
  ...CONVERT_TYPE,
  // Should this just be raq in future?
  ['post-a-job']: 'post_a_job',
  ['post-a-job-landing']: 'post_a_job_landing',
} as const;

export type MessageLocation =
  | 'search'
  | 'message'
  | 'profile'
  | 'post-a-job'
  | 'alternative-trades'
  | 'post-a-job-landing'
  | 'pre-select';

type RequiredTrackingProps = Pick<
  ReturnType<typeof useFunnelling>,
  'funnelConfig' | 'categoryId'
>;
type OptionalTrackingProps = Partial<
  Pick<ReturnType<typeof useFunnelling>, 'fieldValues'>
>;

export const getTrackingPropsFromFunnelling = (
  funnelling: RequiredTrackingProps & OptionalTrackingProps,
) => {
  const postcode = funnelling.funnelConfig?.location;
  return {
    strapi_id: funnelling.funnelConfig?.id,
    class_id: funnelling.categoryId,
    variant_id: funnelling.funnelConfig?.variantId,
    question: funnelling.fieldValues,
    ...(postcode ? { postcode } : {}),
    experiment_id: funnelling.funnelConfig?.experimentId,
  };
};

export const trackMessageFormSubmitted = (
  jobType: JobType,
  {
    companyId,
    categoryId,
    messageLocation,
  }: {
    companyId?: number;
    categoryId?: number;
    messageLocation?: MessageLocation;
  },
) => {
  const isRequestAQuote = jobType === JOB_TYPE.requestAQuote;

  track('job.submitted', {
    type: JOB_TYPES[jobType],
  });

  setConsumerCookie();

  if (jobType === 'post-a-job') {
    return track('send_a_job.form_submit', {
      resource_type: 'send-a-job',
      categoryId,
    });
  }

  if (jobType === 'post-a-job-landing') {
    return track('send_a_job.form_submit', {
      resource_type: 'send-a-job-landing',
      categoryId,
    });
  }

  track(`${JOB_TRACKING_TYPES[jobType]}.form_submit`, {
    ...(isRequestAQuote
      ? { categoryId, resource_type: 'search' }
      : { categoryId, trader_id: companyId }),
    message_location: isRequestAQuote ? null : messageLocation,
  });
};

export const trackMessageSent = (
  jobType: JobType,
  {
    job_id_v2: job_id,
    categoryId,
    postcode,
    companyIds,
    messageLocation,
    chatChannelIds,
    contactPreference,
  }: {
    job_id_v2: string;
    categoryId: number;
    postcode: string;
    companyIds: number[];
    messageLocation?: MessageLocation;
    chatChannelIds?: string[];
    contactPreference?: string;
  },
) => {
  const isRequestAQuote = jobType === JOB_TYPE.requestAQuote;

  logBrazeEvent('JobSubmitted');

  track('job.sent', {
    type: JOB_TYPES[jobType],
    job_id: job_id,
    category_id: categoryId,
    contact_preference: contactPreference,
  });

  setConsumerCookie();

  if (jobType === 'post-a-job') {
    return track('send_a_job.sent', {
      resource_type: 'send-a-job',
      job_id_v2: job_id,
      category_id: categoryId,
      company_ids: companyIds,
      postcode,
      chat_channel_ids: chatChannelIds,
      contact_preference: contactPreference,
    });
  }

  if (jobType === 'post-a-job-landing') {
    return track('send_a_job.sent', {
      resource_type: 'send-a-job-landing',
      job_id_v2: job_id,
      category_id: categoryId,
      company_ids: companyIds,
      postcode,
      chat_channel_ids: chatChannelIds,
      contact_preference: contactPreference,
    });
  }

  return track(`${JOB_TRACKING_TYPES[jobType]}.sent`, {
    job_id_v2: job_id,
    category_id: categoryId,
    company_ids: companyIds,
    postcode,
    message_location: isRequestAQuote ? null : messageLocation,
    chat_channel_ids: chatChannelIds,
    contact_preference: contactPreference,
  });
};
